"use client";

import { logPageView, trackInteractionEvent } from "@/scripts/ga";
import {
	buildAnalyticsFormDataProps,
	extractAnalyticsFormFields,
} from "@/utils/analyticsFormData";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useMemo } from "react";
import { storeTrackingParams } from "@/utils/utmTracker";

const CTA_KEYWORDS = [
	"sign in",
	"signin",
	"log in",
	"login",
	"register",
	"sign up",
	"signup",
	"apply",
	"contact",
	"submit",
	"search",
	"unlock",
	"book",
	"talk to expert",
] as const;

const FORM_SUBMIT_KEYWORDS = [
	"submit",
	"apply",
	"send",
	"register",
	"book",
	"continue",
	"verify",
	"get me in",
	"start",
] as const;

const slugify = (value: string): string =>
	value
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 80);

const readText = (element: HTMLElement): string => {
	const text = (element.getAttribute("aria-label") || element.textContent || "").trim();
	return text.replace(/\s+/g, " ").slice(0, 120);
};

const getClickableElement = (target: EventTarget | null): HTMLElement | null => {
	if (!(target instanceof Element)) return null;
	return target.closest(
		"a, button, input[type='submit'], [role='button'], [data-ga-event]",
	);
};

const isSubmitButton = (element: HTMLElement | null): boolean => {
	if (!element) return false;
	if (element instanceof HTMLButtonElement) {
		return (element.getAttribute("type") || "submit").toLowerCase() === "submit";
	}
	if (element instanceof HTMLInputElement) {
		return element.type.toLowerCase() === "submit";
	}
	return false;
};

const isFormActionButton = (element: HTMLElement | null): boolean => {
	if (!element) return false;
	if (
		!(
			element instanceof HTMLButtonElement ||
			element instanceof HTMLInputElement ||
			element.getAttribute("role") === "button"
		)
	) {
		return false;
	}

	const explicitFormName = element.getAttribute("data-ga-form");
	if (explicitFormName) {
		return true;
	}

	const label = readText(element).toLowerCase();
	return FORM_SUBMIT_KEYWORDS.some((keyword) => label.includes(keyword));
};

const getExplicitFormName = (element: HTMLElement): string | null => {
	return (
		element.getAttribute("data-ga-form") ||
		element.getAttribute("name") ||
		element.getAttribute("id") ||
		element.getAttribute("aria-label")
	);
};

const resolveFormName = (
	formElement: HTMLElement,
	submitter?: HTMLElement | null,
): string => {
	const explicitName = getExplicitFormName(formElement);
	if (explicitName) {
		return slugify(explicitName) || "anonymous_form";
	}

	if (submitter) {
		const submitterName = getExplicitFormName(submitter) || readText(submitter);
		if (submitterName) {
			return slugify(submitterName) || "anonymous_form";
		}
	}

	const firstField = formElement.querySelector("input, textarea, select");
	if (firstField instanceof HTMLElement) {
		const fieldIdentifier =
			firstField.getAttribute("name") ||
			firstField.getAttribute("id") ||
			firstField.getAttribute("placeholder");
		if (fieldIdentifier) {
			return `form_${slugify(fieldIdentifier)}`;
		}
	}

	return "anonymous_form";
};

const getFormLikeContainer = (element: HTMLElement): HTMLElement | null => {
	const nativeForm = element.closest("form");
	if (nativeForm instanceof HTMLElement) {
		return nativeForm;
	}

	let current = element.parentElement;
	while (current && current !== document.body) {
		if (current.querySelector("input, textarea, select")) {
			return current;
		}
		current = current.parentElement;
	}

	return null;
};

const GlobalGATracker = () => {
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const fullPath = useMemo(() => {
		const queryString = searchParams.toString();
		return queryString ? `${pathname}?${queryString}` : pathname;
	}, [pathname, searchParams]);

	useEffect(() => {
		storeTrackingParams();
		if (!fullPath) return;
		logPageView(fullPath);
	}, [fullPath]);

	useEffect(() => {
		if (typeof window === "undefined") return;

		let lastEventKey = "";
		let lastEventAt = 0;

		const shouldSkipDuplicate = (eventKey: string) => {
			const now = Date.now();
			if (eventKey === lastEventKey && now - lastEventAt < 1500) {
				return true;
			}
			lastEventKey = eventKey;
			lastEventAt = now;
			return false;
		};

		const onClick = (event: MouseEvent) => {
			const target = event.target;
			if (!(target instanceof Element)) return;

			// Run tracking asynchronously so we never block browser paint or interaction (INP optimization)
			const scheduleTracking = (cb: () => void) => {
				if ("requestIdleCallback" in window) {
					(window as any).requestIdleCallback(cb, { timeout: 1000 });
				} else {
					setTimeout(cb, 0);
				}
			};

			scheduleTracking(() => {
				const element = getClickableElement(target);
				if (!element) return;

				const formContainer = getFormLikeContainer(element);
				if (formContainer && (isSubmitButton(element) || isFormActionButton(element))) {
					const formName = resolveFormName(formContainer, element);
					const submitText = readText(element);
					const eventKey = `form_submit:${formName}:${window.location.pathname}`;
					if (shouldSkipDuplicate(eventKey)) return;

					trackInteractionEvent("form_submit", {
						form_name: formName,
						submit_text: submitText || undefined,
						submission_mode: formContainer.tagName === "FORM" ? "submit_click" : "button_click",
						page_path: window.location.pathname,
						page_url: window.location.href,
						...buildAnalyticsFormDataProps(extractAnalyticsFormFields(formContainer)),
					});
					return;
				}

				const customEventName = element.getAttribute("data-ga-event");
				const href = element instanceof HTMLAnchorElement ? element.href : "";
				const label = readText(element);
				const lowerLabel = label.toLowerCase();
				const isExternalLink = href ? !href.startsWith(window.location.origin) : false;
				const isContactLink = href.startsWith("tel:") || href.startsWith("mailto:");
				const isCtaByKeyword = CTA_KEYWORDS.some((keyword) =>
					lowerLabel.includes(keyword),
				);

				const eventName =
					customEventName ||
					(isContactLink
						? "contact_link_click"
						: isExternalLink
							? "outbound_link_click"
							: isCtaByKeyword
								? "cta_click"
								: "");

				if (!eventName) return;

				const eventKey = `${eventName}:${label}:${href}:${window.location.pathname}`;
				if (shouldSkipDuplicate(eventKey)) return;

				trackInteractionEvent(eventName, {
					label,
					href: href || undefined,
					page_path: window.location.pathname,
					page_url: window.location.href,
				});
			});
		};

		const onFormSubmit = (event: SubmitEvent) => {
			const formElement = event.target;
			if (!(formElement instanceof HTMLFormElement)) return;
			const submitter =
				event.submitter instanceof HTMLElement ? event.submitter : null;

			const scheduleTracking = (cb: () => void) => {
				if ("requestIdleCallback" in window) {
					(window as any).requestIdleCallback(cb, { timeout: 1000 });
				} else {
					setTimeout(cb, 0);
				}
			};

			scheduleTracking(() => {
				const formName = resolveFormName(formElement, submitter);
				const formAction =
					formElement.getAttribute("action") || window.location.pathname;
				const submitText = submitter ? readText(submitter) : "";

				const eventKey = `form_submit:${formName}:${window.location.pathname}`;
				if (shouldSkipDuplicate(eventKey)) return;

				trackInteractionEvent("form_submit", {
					form_name: formName,
					form_action: formAction || undefined,
					form_method: (formElement.getAttribute("method") || "get").toLowerCase(),
					submit_text: submitText || undefined,
					submission_mode: "form_submit",
					page_path: window.location.pathname,
					page_url: window.location.href,
					...buildAnalyticsFormDataProps(extractAnalyticsFormFields(formElement)),
				});
			});
		};

		document.addEventListener("click", onClick, { capture: true, passive: true });
		document.addEventListener("submit", onFormSubmit, { capture: true, passive: true });

		return () => {
			document.removeEventListener("click", onClick, true);
			document.removeEventListener("submit", onFormSubmit, true);
		};
	}, []);

	return null;
};

export default GlobalGATracker;
